import { ChangeDetectorRef, Directive, HostListener, inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { keydown$ } from '@app/shared/utils';
import { BehaviorSubject, debounceTime, filter, Subscription, switchMap, takeUntil, tap } from 'rxjs';
import { NavItemDirective } from '../nav-item/nav-item.directive';
import { NavItemsService } from '@app/tw-shared/services/nav-items.service';

@Directive({
    selector: '[appNav]',
    providers: [NavItemsService],
})
export class NavDirective implements OnInit, OnDestroy {
    @HostListener('focusin') onFocusin() {
        this.active$.next(true);
    }

    @HostListener('focusout') onFocusout() {
        this.active$.next(false);
    }

    private readonly zone = inject(NgZone);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly navItemsService = inject(NavItemsService);
    private readonly active$ = new BehaviorSubject<boolean>(false);
    private readonly subscriptions$ = new Subscription();

    ngOnInit(): void {
        this.zone.runOutsideAngular(() => {
            this.subscriptions$.add(
                this.navItemsService.items$
                    .pipe(
                        switchMap((items) =>
                            this.active$.pipe(
                                filter((active) => active === true),
                                switchMap(() =>
                                    keydown$.pipe(
                                        takeUntil(
                                            this.active$.pipe(
                                                debounceTime(100),
                                                filter((active) => active === false),
                                            ),
                                        ),
                                        tap((keyboardEvent) => {
                                            this.navItemsService.keyboardEvent$.next(keyboardEvent);
                                            if (keyboardEvent.code === 'ArrowRight') this.handleArrowRight(items);
                                            if (keyboardEvent.code === 'ArrowLeft') this.handleArrowLeft(items);
                                            if (['ArrowDown', 'ArrowUp'].includes(keyboardEvent.code)) keyboardEvent.preventDefault();
                                        }),
                                    ),
                                ),
                            ),
                        ),
                    )
                    .subscribe(),
            );
        });
    }

    ngOnDestroy(): void {
        this.subscriptions$.unsubscribe();
    }

    private handleArrowRight(items: NavItemDirective[]) {
        if (items === null) return;

        const activeIndex = items.findIndex((option) => option.isActive);
        let index = activeIndex + 1;

        const isLast = items[items.length - 1].isActive;

        if (isLast) index = 0;

        this.handleItemFocus(items, index);
    }

    private handleArrowLeft(items: NavItemDirective[]) {
        if (items === null) return;

        const activeIndex = items.findIndex((option) => option.isActive);
        let index = activeIndex - 1;
        const isFirst = items[0].isActive;
        const indexNotFound = index < 0;

        if (isFirst || indexNotFound) index = items.length - 1;

        this.handleItemFocus(items, index);
    }

    private handleItemFocus(items: NavItemDirective[], index: number) {
        this.zone.run(() => {
            items.forEach((item) => item.restart());

            items[index].focus();
            this.cdr.detectChanges();
        });
    }
}
